import { Download, SlidersHorizontal } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import {
  CLOSED_PIPELINE_STAGES,
  PIPELINE_FLAGS,
  PIPELINE_FLAG_LABELS,
  activeFilterCount,
  applicationSourceLabel,
  isBoardStage,
  type PipelineFilter,
  type PipelineFlag,
  type PipelineLayout,
  type PipelineStageView,
  type SavedViewRow,
  type SavedViewState,
  type TagOption,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { SearchInput } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { Segmented } from '../../../components/Segmented';
import { formatDate, formatNumber } from '../../../lib/format';
import { listOf, type FilterChanges } from '../pipeline-search';
import { PipelineFilters } from './PipelineFilters';
import { SavedViewsMenu } from './SavedViewsMenu';
import styles from './PipelineToolbar.module.css';

/** 06-qol §6: type-ahead waits 250ms and needs two characters. */
const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY = 2;
const CLOSED: readonly string[] = CLOSED_PIPELINE_STAGES;

interface PipelineToolbarProps {
  readonly layout: PipelineLayout;
  readonly onLayout: (layout: PipelineLayout) => void;
  readonly filter: PipelineFilter;
  readonly onFilter: (changes: FilterChanges) => void;
  readonly onClear: () => void;
  readonly stages: readonly PipelineStageView[];
  readonly owners: readonly { readonly id: string; readonly fullName: string }[];
  readonly tags: readonly TagOption[];
  readonly viewId: string | undefined;
  readonly viewState: SavedViewState;
  readonly onApplyView: (view: SavedViewRow) => void;
  readonly onExportAll: () => void;
  /**
   * A JOB-12 stage shows one stage only (R2): the stage bar above is its stage chips, so
   * the chips and the board switch go, and the list stays a list.
   */
  readonly isStageLocked?: boolean;
}

/** The strip above SCR-02 and SCR-03: layout, search, filters, saved views, export, and the stage counts. */
export function PipelineToolbar(props: PipelineToolbarProps): ReactNode {
  const { filter, onFilter } = props;
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [query, setQuery] = useState(filter.q ?? '');
  const filterCount = activeFilterCount(filter) - (filter.stage ? 1 : 0) - (filter.q ? 1 : 0);

  // The box follows the address (a saved view, Back) — but never trims what is being typed.
  const [seenQuery, setSeenQuery] = useState(filter.q);
  if (seenQuery !== filter.q) {
    setSeenQuery(filter.q);
    if ((filter.q ?? '') !== query.trim()) setQuery(filter.q ?? '');
  }
  useEffect(() => {
    const trimmed = query.trim();
    const wanted = trimmed.length >= MIN_QUERY ? trimmed : undefined;
    if (wanted === filter.q) return undefined;
    const timer = window.setTimeout(() => onFilter({ q: wanted }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, filter.q, onFilter]);

  return (
    <div className={styles.toolbar}>
      <div className={styles.row}>
        {!props.isStageLocked && (
          <Segmented
            label="Layout"
            value={props.layout}
            options={[
              { value: 'list', label: 'List' },
              { value: 'board', label: 'Board' },
            ]}
            onChange={props.onLayout}
          />
        )}
        <div className={styles.search}>
          <SearchInput value={query} onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} placeholder="Name, email, mobile or reference" data-primary-search />
        </div>
        <span className={styles.spacer} />
        <Button ref={triggerRef} variant="quiet" icon={SlidersHorizontal} onClick={toggle} aria-haspopup="dialog" aria-expanded={isOpen}>
          Filters{filterCount > 0 ? ` · ${filterCount}` : ''}
        </Button>
        <SavedViewsMenu currentId={props.viewId} current={props.viewState} onApply={props.onApplyView} />
        <Button variant="quiet" icon={Download} onClick={props.onExportAll}>
          Export
        </Button>
      </div>

      {props.layout === 'list' && !props.isStageLocked && <StageChips stages={props.stages} value={filter.stage} onChange={(value) => onFilter({ stage: value })} />}

      <ActiveFilters filter={filter} owners={props.owners} tags={props.tags} onFilter={onFilter} onClear={props.onClear} />

      <Popover anchor={anchor} onClose={close} align="right" label="Filters">
        <PipelineFilters
          filter={filter}
          owners={props.owners}
          tags={props.tags}
          onChange={(key, value) => onFilter({ [key]: value })}
          onClear={() => {
            props.onClear();
            close();
          }}
        />
      </Popover>
    </div>
  );
}

/** "Counts are shown, not hinted": every stage with its number, then the closed ones, then everyone. */
function StageChips({ stages, value, onChange }: { readonly stages: readonly PipelineStageView[]; readonly value: string | undefined; readonly onChange: (value: string | undefined) => void }): ReactNode {
  const open = stages.filter((stage) => isBoardStage(stage.stage));
  const closedOnes = stages.filter((stage) => CLOSED.includes(stage.stage));
  const sum = (list: readonly PipelineStageView[]): number => list.reduce((total, stage) => total + stage.count, 0);
  const rejected = closedOnes.find((stage) => stage.stage === 'rejected');
  const chips: readonly { key: string; label: string; count: number; value: string | undefined }[] = [
    { key: 'active', label: 'Active', count: sum(open), value: undefined },
    ...open.filter((stage) => stage.count > 0 || stage.stage !== 'new').map((stage) => ({ key: stage.stage, label: stage.label, count: stage.count, value: stage.stage })),
    { key: 'rejected', label: rejected?.label ?? 'Rejected', count: rejected?.count ?? 0, value: 'rejected' },
    { key: 'closed', label: 'Closed', count: sum(closedOnes), value: 'closed' },
    { key: 'all', label: 'All', count: sum(stages), value: 'all' },
  ];
  const current = value ?? undefined;
  return (
    <div className={styles.stages} role="tablist" aria-label="Stage">
      {chips.map((chip) => {
        const isCurrent = chip.value === current || (chip.value === undefined && (current === undefined || current === 'active'));
        return (
          <button key={chip.key} type="button" role="tab" aria-selected={isCurrent} className={isCurrent ? styles.stageCurrent : styles.stage} onClick={() => onChange(chip.value)}>
            {chip.label}
            <span className={styles.stageCount}>{formatNumber(chip.count)}</span>
          </button>
        );
      })}
    </div>
  );
}

function ActiveFilters({
  filter,
  owners,
  tags,
  onFilter,
  onClear,
}: {
  readonly filter: PipelineFilter;
  readonly owners: readonly { readonly id: string; readonly fullName: string }[];
  readonly tags: readonly TagOption[];
  readonly onFilter: (changes: FilterChanges) => void;
  readonly onClear: () => void;
}): ReactNode {
  const chips: { key: keyof PipelineFilter; label: string }[] = [];
  if (filter.scoreMin || filter.scoreMax) chips.push({ key: 'scoreMin', label: `Match ${filter.scoreMin ?? '0'}–${filter.scoreMax ?? '100'}` });
  if (filter.source) chips.push({ key: 'source', label: `Source: ${listOf(filter.source).map((source) => applicationSourceLabel(source)).join(', ')}` });
  if (filter.recruiter) chips.push({ key: 'recruiter', label: filter.recruiter === 'none' ? 'No recruiter yet' : `Recruiter: ${owners.find((owner) => owner.id === filter.recruiter)?.fullName ?? 'someone'}` });
  if (filter.tag) chips.push({ key: 'tag', label: `Tagged ${listOf(filter.tag).map((id) => tags.find((tag) => tag.id === id)?.name ?? 'tag').join(' or ')}` });
  if (filter.flag) chips.push({ key: 'flag', label: listOf(filter.flag).filter(isFlag).map((flag) => PIPELINE_FLAG_LABELS[flag]).join(', ') });
  if (filter.appliedFrom || filter.appliedTo) chips.push({ key: 'appliedFrom', label: `Applied ${filter.appliedFrom ? `from ${formatDate(filter.appliedFrom)}` : ''}${filter.appliedTo ? ` to ${formatDate(filter.appliedTo)}` : ''}` });
  if (chips.length === 0) return null;

  function remove(key: keyof PipelineFilter): void {
    if (key === 'scoreMin') return onFilter({ scoreMin: undefined, scoreMax: undefined });
    if (key === 'appliedFrom') return onFilter({ appliedFrom: undefined, appliedTo: undefined });
    onFilter({ [key]: undefined });
  }

  return (
    <div className={styles.active}>
      <span className={styles.activeLabel}>Filters</span>
      {chips.map((chip) => (
        <Chip key={chip.key} plain onRemove={() => remove(chip.key)} removeLabel={`Remove ${chip.label}`}>
          {chip.label}
        </Chip>
      ))}
      <button type="button" className={styles.clearAll} onClick={onClear}>
        Clear all
      </button>
    </div>
  );
}

function isFlag(value: string): value is PipelineFlag {
  return (PIPELINE_FLAGS as readonly string[]).includes(value);
}
